import {
  AnchorButton,
  Button,
  Callout,
  Classes,
  Divider,
  Drawer,
  Intent,
  Label,
  Position,
} from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { Route } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import {
  QueryBuilderContext,
  QueryBuilderContextValue,
  QueryBuilderModelContext,
  QueryBuilderSchemaContext,
  TopExpressionBlock,
} from 'vet-bones/components';
import { isEqual } from 'vet-bones/utils';
import {
  CURRENT_QUERY_BUILDER_VERSION,
  DraftTopExpression,
  QueryBuilder,
  addDraftFields,
  createNewTopExpression,
  getUsedVariables,
  removeDraftFields,
  updateValidation,
} from 'vet-bones/utils/queryBuilder';

import { EditableLabel } from 'src/components/EditableLabel';
import { ROUTE } from 'src/constants/routes';
import { TEST_IDS } from 'src/constants/testIds';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { commitDiagramModel } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { addRule } from 'src/containers/ProjectDiagram/utils/rules/addRule';
import { getValidThenModel } from 'src/containers/ProjectDiagram/utils/rules/getValidThenModel';
import { removeRule } from 'src/containers/ProjectDiagram/utils/rules/removeRule';
import { updateRule } from 'src/containers/ProjectDiagram/utils/rules/updateRule';
import {
  IF_QUERY_BUILDER_SCHEMA,
  THEN_QUERY_BUILDER_SCHEMA,
} from 'src/containers/ProjectRuleDetailDrawer/constants';
import { projectRuleModelSelector } from 'src/selectors/projectRuleModel';
import { projectRuleSelector } from 'src/selectors/projectRules';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

const ProjectRuleDetailDrawerBody = () => {
  const { projectId } = useRouterParams();
  const diagram = useDiagram();
  const dispatch = useReduxDispatch();

  const existingRuleId = useReduxSelector(
    (state) => state.ui.ruleDetailDrawer.ruleId || ''
  );
  const existingRule = useReduxSelector(
    projectRuleSelector(projectId, existingRuleId)
  );
  const didExistingRuleChange = useDidValueChange(existingRule, isEqual);
  const validModel = useReduxSelector(
    projectRuleModelSelector(projectId),
    isEqual
  );

  const validThenModel = React.useMemo(
    () => getValidThenModel(validModel),
    [validModel]
  );

  const [name, setName] = React.useState(existingRule?.name || '');
  const [didEditName, setDidEditName] = React.useState(false);

  const { isValid: nextIfValid, topExpression: nextIfValue } =
    existingRule?.ifClause
      ? updateValidation({
          copy: IF_QUERY_BUILDER_SCHEMA.copy,
          topExpression: addDraftFields(existingRule.ifClause.data.query),
          validModel,
        })
      : { isValid: true, topExpression: createNewTopExpression() };
  const nextIfVariables = getUsedVariables(nextIfValue);

  const [localIfValue, setLocalIfValue] = React.useState(nextIfValue);
  const [ifVariables, setIfVariables] = React.useState(nextIfVariables);

  const { isValid: nextThenValid, topExpression: nextThenValue } =
    existingRule?.thenClause
      ? updateValidation({
          copy: THEN_QUERY_BUILDER_SCHEMA.copy,
          topExpression: addDraftFields(existingRule.thenClause.data.query),
          validModel: validThenModel,
          validVariables: nextIfVariables,
        })
      : { isValid: true, topExpression: createNewTopExpression() };
  const nextThenVariables = getUsedVariables(nextThenValue);

  const [localThenValue, setLocalThenValue] = React.useState(nextThenValue);
  const [thenVariables, setThenVariables] = React.useState(nextThenVariables);

  const showReasoningWarning = React.useMemo(
    () =>
      localIfValue.expressions.some(
        (subExpression) => subExpression.expressions.length > 1
      ),
    [localIfValue]
  );

  const [showValidation, setShowValidation] = React.useState(
    existingRule && (!nextIfValid || !nextThenValid)
  );

  React.useEffect(() => {
    if (didExistingRuleChange) {
      setName(existingRule?.name || '');
      setDidEditName(false);

      setLocalIfValue(nextIfValue);
      setIfVariables(nextIfVariables);

      setLocalThenValue(nextThenValue);
      setThenVariables(nextThenVariables);

      setShowValidation(!nextIfValid || !nextThenValid);
    }
  }, [
    didExistingRuleChange,
    existingRule,
    nextIfValid,
    nextIfValue,
    nextIfVariables,
    nextThenValid,
    nextThenValue,
    nextThenVariables,
    validModel,
  ]);

  const handleSetName = (newName: string) => {
    setName(newName);
  };

  const handleBlur = (newValue: string) => {
    if (existingRule ? newValue !== existingRule.name : newValue === '') {
      setDidEditName(true);
    }
  };

  const handleValidateName = React.useCallback(
    (name: string) => {
      if (!didEditName) {
        return '';
      }

      return name.trim()
        ? ''
        : copy.components.projectRuleDetailDrawer.validation.nameRequired;
    },
    [didEditName]
  );

  const setThenValue = React.useCallback(
    (topExpression: DraftTopExpression) => {
      setLocalThenValue(topExpression);
      const nextThenValues = getUsedVariables(topExpression);
      setThenVariables(nextThenValues);
    },
    []
  );

  const handleThenValidation = React.useCallback(
    ({
      nextTopExpression,
      validVariables,
      skipOperator,
    }: {
      nextTopExpression: DraftTopExpression;
      validVariables: Record<string, string[]>;
      skipOperator?: boolean;
    }) => {
      const { topExpression, isValid } = updateValidation({
        copy: THEN_QUERY_BUILDER_SCHEMA.copy,
        topExpression: nextTopExpression,
        skipOperator,
        validModel: validThenModel,
        validVariables,
      });
      setThenValue(topExpression);
      return { topExpression, isValid };
    },
    [setThenValue, validThenModel]
  );

  const handleSetThen = React.useCallback(
    (nextTopExpression: DraftTopExpression, validate?: boolean) => {
      if (validate) {
        handleThenValidation({
          nextTopExpression,
          validVariables: ifVariables,
          skipOperator: true,
        });
      } else {
        setThenValue(nextTopExpression);
      }
    },
    [handleThenValidation, ifVariables, setThenValue]
  );

  const setIfValue = React.useCallback((topExpression: DraftTopExpression) => {
    setLocalIfValue(topExpression);
    const nextIfVariables = getUsedVariables(topExpression);
    setIfVariables(nextIfVariables);
  }, []);

  const handleIfValidation = React.useCallback(
    ({
      nextTopExpression,
      skipOperator,
    }: {
      nextTopExpression: DraftTopExpression;
      skipOperator?: boolean;
    }) => {
      const { topExpression, isValid } = updateValidation({
        copy: IF_QUERY_BUILDER_SCHEMA.copy,
        topExpression: nextTopExpression,
        skipOperator,
        validModel,
      });
      setIfValue(topExpression);
      return { topExpression, isValid };
    },
    [setIfValue, validModel]
  );

  const handleSetIf = React.useCallback(
    (nextTopExpression: DraftTopExpression, validate?: boolean) => {
      if (validate) {
        handleIfValidation({ nextTopExpression, skipOperator: true });
      } else {
        setIfValue(nextTopExpression);
      }
    },
    [handleIfValidation, setIfValue]
  );

  const onClose = () => {
    setName('');
    setDidEditName(false);
    setLocalIfValue(createNewTopExpression());
    setIfVariables({});
    setLocalThenValue(createNewTopExpression());
    setThenVariables({});
    setShowValidation(false);
    dispatch(uiSlice.actions.toggledRuleDetailDrawer({ isOpen: false }));
  };

  const onSubmit = () => {
    if (!diagram) {
      return;
    }

    if (!name) {
      setDidEditName(true);
    }

    const ifClauseValidation = handleIfValidation({
      nextTopExpression: localIfValue,
    });
    const thenClauseValidation = handleThenValidation({
      nextTopExpression: localThenValue,
      validVariables: ifVariables,
    });
    if (!name || !ifClauseValidation.isValid || !thenClauseValidation.isValid) {
      setShowValidation(true);
      return;
    }

    const ifClauseData: QueryBuilder = {
      version: CURRENT_QUERY_BUILDER_VERSION,
      data: { query: removeDraftFields(localIfValue), variableIds: [] },
    };

    const thenClauseData: QueryBuilder = {
      version: CURRENT_QUERY_BUILDER_VERSION,
      data: { query: removeDraftFields(localThenValue), variableIds: [] },
    };

    if (existingRuleId) {
      commitDiagramModel(
        diagram,
        updateRule({
          id: existingRuleId,
          name,
          ifClause: ifClauseData,
          thenClause: thenClauseData,
        })
      );
    } else {
      commitDiagramModel(
        diagram,
        addRule({
          id: uuid(),
          name,
          ifClause: ifClauseData,
          thenClause: thenClauseData,
        })
      );
    }
    onClose();
  };

  const ifCtx = React.useMemo(
    (): QueryBuilderContextValue => ({
      topExpression: localIfValue,
      otherVariables: thenVariables,
    }),
    [localIfValue, thenVariables]
  );

  const thenCtx = React.useMemo(
    (): QueryBuilderContextValue => ({
      topExpression: localThenValue,
      validVariables: ifVariables,
      otherVariables: ifVariables,
    }),
    [localThenValue, ifVariables]
  );

  const isSaveDisabled = React.useMemo(
    () =>
      Boolean(
        existingRule &&
          isEqual(name, existingRule.name) &&
          isEqual(
            removeDraftFields(localIfValue),
            existingRule.ifClause.data.query
          ) &&
          isEqual(
            removeDraftFields(localThenValue),
            existingRule.thenClause.data.query
          )
      ),
    [existingRule, localIfValue, name, localThenValue]
  );

  const handleDelete = () => {
    if (!diagram || !existingRule) {
      return;
    }
    commitDiagramModel(diagram, removeRule(existingRule.id));
    onClose();
  };

  return (
    <>
      <div
        className={Classes.DRAWER_BODY}
        data-testid={TEST_IDS.projectRuleDetailDrawer.container}
      >
        <Label className="sd-drawer-editable-label">
          {copy.components.projectRuleDetailDrawer.name}
          <div className={Classes.CALLOUT}>
            <EditableLabel
              key={`${didEditName}`}
              onBlur={handleBlur}
              onSetValue={handleSetName}
              onValidate={handleValidateName}
              placeholder={
                existingRule?.name ||
                copy.components.projectRuleDetailDrawer.newRule
              }
              setValueOnChange
              value={name}
            />
          </div>
        </Label>

        <Divider />

        <div data-testid={TEST_IDS.projectRuleDetailDrawer.ifClause}>
          {copy.components.projectRuleDetailDrawer.ifClause}
          <QueryBuilderSchemaContext.Provider value={IF_QUERY_BUILDER_SCHEMA}>
            <QueryBuilderModelContext.Provider value={validModel}>
              <QueryBuilderContext.Provider value={ifCtx}>
                <TopExpressionBlock setTopExpression={handleSetIf} />
              </QueryBuilderContext.Provider>
            </QueryBuilderModelContext.Provider>
          </QueryBuilderSchemaContext.Provider>
        </div>

        <div data-testid={TEST_IDS.projectRuleDetailDrawer.thenClause}>
          {copy.components.projectRuleDetailDrawer.thenClause}
          <QueryBuilderSchemaContext.Provider value={THEN_QUERY_BUILDER_SCHEMA}>
            <QueryBuilderModelContext.Provider value={validThenModel}>
              <QueryBuilderContext.Provider value={thenCtx}>
                <TopExpressionBlock setTopExpression={handleSetThen} />
              </QueryBuilderContext.Provider>
            </QueryBuilderModelContext.Provider>
          </QueryBuilderSchemaContext.Provider>
        </div>

        {showReasoningWarning ? (
          <div data-testid={TEST_IDS.projectRuleDetailDrawer.reasoningWarning}>
            <Callout intent={Intent.PRIMARY}>
              {copy.components.projectRuleDetailDrawer.reasoningWarning}
            </Callout>
          </div>
        ) : null}

        {showValidation ? (
          <div data-testid={TEST_IDS.projectRuleDetailDrawer.error}>
            <Callout intent={Intent.DANGER}>
              {copy.components.projectRuleDetailDrawer.validation.incomplete}
            </Callout>
          </div>
        ) : null}
      </div>
      <div className={classNames(Classes.DRAWER_FOOTER)}>
        <div className="sd-drawer-footer-actions">
          {existingRule ? (
            <Button
              data-testid={TEST_IDS.projectRuleDetailDrawer.deleteButton}
              onClick={handleDelete}
              outlined
              text={copy.components.projectRuleDetailDrawer.delete}
            />
          ) : null}
        </div>
        <div className="sd-drawer-footer-actions">
          <Button
            data-testid={TEST_IDS.projectRuleDetailDrawer.cancelButton}
            intent={Intent.PRIMARY}
            onClick={onClose}
            outlined
            text={copy.components.projectRuleDetailDrawer.cancel}
          />
          <AnchorButton
            data-testid={TEST_IDS.projectRuleDetailDrawer.submitButton}
            disabled={isSaveDisabled}
            intent={Intent.PRIMARY}
            onClick={onSubmit}
            text={copy.components.projectRuleDetailDrawer.submit}
          />
        </div>
      </div>
    </>
  );
};

export const ProjectRuleDetailDrawer: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const isOpen = useReduxSelector((state) => state.ui.ruleDetailDrawer.isOpen);

  const handleClose = React.useCallback(() => {
    dispatch(uiSlice.actions.toggledRuleDetailDrawer({ isOpen: false }));
  }, [dispatch]);

  return (
    <Drawer
      autoFocus={false}
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      className="sd-drawer-project-rule-detail"
      enforceFocus={false}
      isOpen={isOpen}
      lazy={false}
      onClose={handleClose}
      position={Position.RIGHT}
      shouldReturnFocusOnClose={false}
      title={copy.components.projectRuleDetailDrawer.title}
    >
      <Route path={ROUTE.PROJECT}>
        <ProjectRuleDetailDrawerBody />
      </Route>
    </Drawer>
  );
};
